/**
 * ตัวเชื่อม MQTT ที่มือถือกับจอใช้ร่วมกัน
 *
 * HTML เป็นฝ่ายต่อ wss เอง — Cloudflare ไม่อยู่ใน path นี้เลย
 * ไม่มี Worker publish/subscribe · ไม่มี bridge · ไม่มี endpoint แจก credential
 * (ARCHITECTURE.md §2.3, CLAUDE.md กฎเหล็กข้อ 8)
 *
 * credential มาจาก build-time env และคุมสิทธิ์ที่ broker ฝ่ายเดียว
 */
import mqtt, { type IClientOptions, type MqttClient } from "mqtt";

import { RECONNECT_MAX_MS, RECONNECT_MIN_MS } from "@py/protocol";

export type Will = { topic: string; payload: string };

export type ClientConfig = {
  url: string;
  username: string;
  password: string;
  clientId: string;
  will?: Will;
  onStatus?: (s: "connecting" | "online" | "offline") => void;
};

export type Handler = (topic: string, payload: Uint8Array) => void;

export class Bus {
  private client: MqttClient | null = null;
  private readonly subs = new Map<string, Set<Handler>>();
  private backoff = RECONNECT_MIN_MS;
  private closed = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly cfg: ClientConfig) {}

  get connected(): boolean {
    return this.client?.connected ?? false;
  }

  connect(): void {
    if (this.closed) return;
    this.cfg.onStatus?.("connecting");

    const opts: IClientOptions = {
      clientId: this.cfg.clientId,
      username: this.cfg.username,
      password: this.cfg.password,
      protocolVersion: 5,
      clean: true,
      keepalive: 15,
      // ปิด auto-reconnect ของ lib แล้วทำ backoff เองจะคุมจังหวะได้ชัดกว่า
      reconnectPeriod: 0,
      connectTimeout: 8000,
    };
    if (this.cfg.will) {
      opts.will = {
        topic: this.cfg.will.topic,
        payload: this.cfg.will.payload,
        qos: 1,
        retain: true,
      };
    }

    const c = mqtt.connect(this.cfg.url, opts);
    this.client = c;

    c.on("connect", () => {
      this.backoff = RECONNECT_MIN_MS;
      this.cfg.onStatus?.("online");
      for (const filter of this.subs.keys()) c.subscribe(filter, { qos: 0 });
    });

    c.on("message", (topic, payload) => {
      for (const [filter, hs] of this.subs) {
        if (!matches(filter, topic)) continue;
        for (const h of hs) h(topic, new Uint8Array(payload));
      }
    });

    const retry = () => {
      if (this.closed) return;
      this.cfg.onStatus?.("offline");
      c.removeAllListeners();
      try { c.end(true); } catch { /* ปิดไปแล้วก็ไม่เป็นไร */ }
      this.client = null;
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => this.connect(), this.backoff);
      this.backoff = Math.min(this.backoff * 2, RECONNECT_MAX_MS);
    };

    c.on("error", retry);
    c.on("close", retry);
  }

  subscribe(filter: string, h: Handler): () => void {
    let set = this.subs.get(filter);
    if (!set) {
      set = new Set();
      this.subs.set(filter, set);
      this.client?.subscribe(filter, { qos: 0 });
    }
    set.add(h);
    return () => {
      set!.delete(h);
      if (set!.size === 0) {
        this.subs.delete(filter);
        this.client?.unsubscribe(filter);
      }
    };
  }

  publish(topic: string, payload: string, qos: 0 | 1 = 0, retain = false): void {
    // ช่วง reconnect ห้ามยัดใส่ socket ที่กำลังปิด — mqtt.js จะพ่น
    // "WebSocket is already in CLOSING" รัวๆ และแพ็กเก็ตก็หายอยู่ดี
    if (!this.client?.connected) return;
    this.client.publish(topic, payload, { qos, retain });
  }

  close(): void {
    this.closed = true;
    if (this.timer) clearTimeout(this.timer);
    try { this.client?.end(true); } catch { /* ไม่มีอะไรต้องทำ */ }
    this.client = null;
  }
}

/** จับคู่ topic filter ของ MQTT (`+` = ระดับเดียว, `#` = ที่เหลือทั้งหมด) */
export function matches(filter: string, topic: string): boolean {
  const f = filter.split("/");
  const t = topic.split("/");
  for (let i = 0; i < f.length; i++) {
    if (f[i] === "#") return true;
    if (i >= t.length) return false;
    if (f[i] !== "+" && f[i] !== t[i]) return false;
  }
  return f.length === t.length;
}
